import { useEffect, useMemo, useRef, useState } from 'react';
import { MessageCircle, Send, X } from 'lucide-react';
import { useWorkspaceAssistant } from '../../assistant/useGraphAssistant';
import { UpgradeModal } from '../../../components/UpgradeModal';
import { productMessaging } from '../../../config/messaging';
import { useGraph, useLastResult } from '../../../store/simulationStore';
import { useScenarios } from '../../../store/scenarioStore';

const renderTextWithBreaks = (text: string) => {
  const lines = text.split('\n');
  return lines.map((line, index) => (
    <span key={index}>
      {line}
      {index < lines.length - 1 && <br />}
    </span>
  ));
};

export const AssistantPanel = () => {
  const {
    messages,
    sendMessage,
    isLoading,
    error,
    configStatus,
    showUpgradeModal,
    setShowUpgradeModal
  } = useWorkspaceAssistant();

  const [input, setInput] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);
  const graph = useGraph();
  const result = useLastResult();
  const scenarios = useScenarios();
  const previousMessageCountRef = useRef(messages.length);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const outputCount = graph.nodes.filter((node) => node.data.isOutput).length;

  const promptChips = useMemo(() => {
    if (graph.nodes.length === 0) {
      return productMessaging.assistantPrompts.emptyGraph;
    }
    if (outputCount === 0) {
      return productMessaging.assistantPrompts.noOutputs;
    }
    if (scenarios.some((scenario) => !scenario.isBaseline)) {
      return productMessaging.assistantPrompts.scenarios;
    }
    if (result) {
      return productMessaging.assistantPrompts.hasResults;
    }
    return productMessaging.assistantPrompts.noOutputs;
  }, [graph.nodes.length, outputCount, result, scenarios]);

  useEffect(() => {
    if (scrollRef.current && isOpen) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [isOpen, messages]);

  useEffect(() => {
    if (messages.length > previousMessageCountRef.current && !isOpen) {
      setHasUnread(true);
    }
    if (isOpen) {
      setHasUnread(false);
    }
    previousMessageCountRef.current = messages.length;
  }, [isOpen, messages.length]);

  useEffect(() => {
    const handleOpenAssistant = (event: Event) => {
      const customEvent = event as CustomEvent<{ prompt?: string }>;
      setIsOpen(true);
      if (customEvent.detail?.prompt) {
        setInput(customEvent.detail.prompt);
        requestAnimationFrame(() => textareaRef.current?.focus());
      }
    };

    window.addEventListener('simcarlo:open-assistant', handleOpenAssistant);
    return () => window.removeEventListener('simcarlo:open-assistant', handleOpenAssistant);
  }, []);

  const statusLabel = useMemo(() => {
    if (!configStatus.isConfigured) {
      return `Missing configuration: ${configStatus.missing.join(', ')}`;
    }
    if (isLoading) {
      return 'Working...';
    }
    return error ? 'Error' : 'Ready';
  }, [configStatus, error, isLoading]);

  const statusClass = useMemo(() => {
    if (!configStatus.isConfigured) return 'assistant-status missing';
    if (error) return 'assistant-status error';
    if (isLoading) return 'assistant-status loading';
    return 'assistant-status ready';
  }, [configStatus, error, isLoading]);

  const toggleOpen = () => {
    setIsOpen((current) => !current);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = input.trim();
    if (!trimmed) return;
    await sendMessage(trimmed);
    setInput('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(event.target.value);
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  };

  const handlePromptChip = async (prompt: string) => {
    if (!configStatus.isConfigured || isLoading) {
      setInput(prompt);
      return;
    }
    await sendMessage(prompt);
  };

  return (
    <div className={`workspace-assistant ${isOpen ? 'is-open' : ''}`}>
      {isOpen && (
        <section
          className="workspace-assistant-panel assistant-panel"
          role="dialog"
          aria-label="Workspace assistant"
        >
          <div className="workspace-assistant-header">
            <div className="assistant-header-left">
              <span className="sidebar-section-label">Assistant</span>
              <span className={statusClass}>{statusLabel}</span>
            </div>
            <button
              type="button"
              className="workspace-assistant-close"
              onClick={() => setIsOpen(false)}
              aria-label="Close assistant"
              title="Close assistant"
            >
              <X size={16} strokeWidth={2.25} aria-hidden="true" />
            </button>
          </div>

          <div className="assistant-messages" ref={scrollRef}>
            {!configStatus.isConfigured && (
              <div className="assistant-banner warning">
                OpenAI credentials are missing. Set the environment variables to enable chat.
              </div>
            )}
            {error && (
              <div className="assistant-banner error">{error}</div>
            )}
            {messages.map((message) => (
              <div key={message.id} className={`assistant-message ${message.role}`}>
                <div className="assistant-message-role">
                  {message.role === 'user' ? 'You' : 'Assistant'}
                </div>
                <div className="assistant-message-text">
                  {renderTextWithBreaks(message.text)}
                </div>
                {!!message.warnings?.length && (
                  <div className="assistant-message-warnings">
                    {message.warnings.map((warning, index) => (
                      <span key={index} className="assistant-warning-chip">
                        {warning}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {!messages.length && (
              <div className="assistant-empty">
                <p>Ask about the current model, inputs, scenarios, results, or request a graph edit.</p>
                <div className="assistant-prompt-chips" aria-label="Suggested assistant prompts">
                  {promptChips.map((prompt) => (
                    <button
                      key={prompt}
                      type="button"
                      className="assistant-prompt-chip"
                      onClick={() => void handlePromptChip(prompt)}
                      disabled={isLoading}
                    >
                      {prompt}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <form className="assistant-form" onSubmit={handleSubmit}>
            <div className="assistant-input-wrapper">
              <textarea
                ref={textareaRef}
                value={input}
                onChange={handleInputChange}
                placeholder={
                  configStatus.isConfigured
                    ? 'Ask anything about this model...'
                    : 'Configure OpenAI variables before chatting.'
                }
                disabled={!configStatus.isConfigured || isLoading}
                className="assistant-input"
                rows={1}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    handleSubmit(event as unknown as React.FormEvent<HTMLFormElement>);
                  }
                }}
              />
              <button
                type="submit"
                className="assistant-send-button"
                disabled={!input.trim() || isLoading || !configStatus.isConfigured}
                title={isLoading ? 'Thinking...' : 'Send'}
                aria-label="Send assistant message"
              >
                <Send size={18} strokeWidth={2.25} aria-hidden="true" />
              </button>
            </div>
          </form>
        </section>
      )}

      <button
        type="button"
        className={`assistant-floating-button ${error ? 'has-error' : ''} ${isLoading ? 'is-loading' : ''} ${hasUnread ? 'has-unread' : ''}`}
        onClick={toggleOpen}
        aria-label={isOpen ? 'Close assistant' : 'Open assistant'}
        aria-expanded={isOpen}
        title={isOpen ? 'Close assistant' : 'Open assistant'}
      >
        {isOpen ? (
          <X size={22} strokeWidth={2.25} aria-hidden="true" />
        ) : (
          <MessageCircle size={22} strokeWidth={2.25} aria-hidden="true" />
        )}
        <span className="assistant-floating-indicator" aria-hidden="true" />
      </button>

      {showUpgradeModal && (
        <UpgradeModal
          reason="assistant_limit"
          onClose={() => setShowUpgradeModal(false)}
        />
      )}
    </div>
  );
};
